import styled, { CSSObject } from "@emotion/styled";
import { padding as polishedPadding } from "polished";

import {
  BodyVariantOpts,
  BodyVariants,
  FontWeightOpts,
} from "@brexhq/shared/styles/fonts";
import type { DefaultTheme } from "@brexhq/shared/styles/theming";

interface PaddingObject {
  large?: Array<string | number>;
  medium?: Array<string | number>;
  small?: Array<string | number>;
}

interface BaseTextProps {
  color?: string;
  uppercase?: boolean;
  keepSameSize?: boolean;
  padding?: PaddingObject;
  responsiveText?: ResponsiveText;
  weight?: FontWeightOpts;
}

type TextProperties = {
  size: string | number;
  lineHeight: string;
};
interface ResponsiveText {
  small?: TextProperties;
  medium?: TextProperties;
}

interface BodyText extends BaseTextProps {
  textModifier?: BodyVariantOpts;
  keepSameSize?: boolean;
  width?: React.CSSProperties["width"];
}

const baseTextStyles = (
  color: string | undefined,
  uppercase = false,
  theme: DefaultTheme,
  padding?: PaddingObject,
  responsiveText?: ResponsiveText,
  keepSameSize?: boolean,
  weight?: FontWeightOpts,
): CSSObject => {
  return {
    margin: 0,
    color: color ?? theme.colors.content.primary,
    textTransform: uppercase ? "uppercase" : "none",
    ...(weight && {
      fontVariationSettings: theme.fonts.FontWeight[weight],
    }),
    ...(padding?.large && polishedPadding(...padding.large)),
    [theme.breakpoints.MED]: {
      ...(padding?.medium && polishedPadding(...padding.medium)),
      ...(responsiveText?.medium &&
        !keepSameSize && {
          fontSize: responsiveText?.medium?.size,
          lineHeight: responsiveText?.medium?.lineHeight,
        }),
    },
    [theme.breakpoints.SML]: {
      ...(padding?.small && polishedPadding(...padding.small)),
      ...(responsiveText?.small &&
        !keepSameSize && {
          fontSize: responsiveText?.small?.size,
          lineHeight: responsiveText?.small?.lineHeight,
        }),
    },
  };
};

/**
 * @deprecated
 */
export const H5Caption = styled.h5<BaseTextProps>(
  ({ theme, color, uppercase, padding, keepSameSize, weight }) => {
    const responsiveRules = {};
    return {
      ...theme.fonts.primaryFonts.h5,
      ...baseTextStyles(
        color,
        uppercase,
        theme,
        padding,
        responsiveRules,
        keepSameSize,
        weight,
      ),
    };
  },
);

const bodyBodifierSize = (modifier: string): string => {
  if (modifier.includes("LESS")) return "XL_LESS";
  if (modifier.includes("XL")) return "XL";
  if (modifier.includes("MED")) return "MED";
  if (modifier.includes("LRG_BOOK")) return "LRG_BOOK";
  if (modifier.includes("LRG")) return "LRG";
  if (modifier.includes("SML")) return "SML";
  if (modifier.includes("MICRO")) return "MICRO";
  return "";
};

/**
 * @deprecated
 */
export const BodyText = styled.p<BodyText>(
  ({
    theme,
    color,
    textModifier = "MED_BOOK",
    uppercase,
    padding,
    keepSameSize = false,
    width,
  }) => {
    const modifier = BodyVariants[textModifier];
    const modifierSize = bodyBodifierSize(textModifier);

    return {
      ...theme.fonts.primaryFonts[modifier],
      width,
      ...baseTextStyles(
        color,
        uppercase,
        theme,
        padding,
        bodyResponsive[modifierSize],
        keepSameSize,
      ),
    };
  },
);

/**
 * @deprecated
 */
export const Span = styled.span<BaseTextProps>(({ theme, color, weight }) => {
  return {
    color: color ?? "inherit",
    fontVariationSettings: weight ? theme.fonts.FontWeight[weight] : "inherit",
  };
});

const bodyResponsive = {
  XL: {
    small: {
      size: 16,
      lineHeight: "26px",
    },
    medium: {
      size: 18,
      lineHeight: "28px",
    },
  },
  XL_LESS: {
    small: {
      size: 14,
      lineHeight: "24px",
    },
    medium: {
      size: 16,
      lineHeight: "27px",
    },
  },
  MED: {
    small: {
      size: 10,
      lineHeight: "20px",
    },
    medium: {
      size: 12,
      lineHeight: "20px",
    },
  },
  SML: {
    small: {
      size: 10,
      lineHeight: "20px",
    },
  },
  // MICRO SIZE SAME
  MICRO: {},
  LRG_BOOK: {
    small: {
      size: 14,
      lineHeight: "20px",
    },
  },
};
